'use client';

import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faFolderBookmark,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { Checkbox, Input, CircularProgress } from 'src/components/PulseUI';
import { ClickAwayListener } from '../ClickAwayListener';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { twMerge } from 'tailwind-merge';

export interface OptionType {
  value: string;
  label: string;
}

// Replicate size type from Select for consistency
type AutocompleteSize = 'sm' | 'md' | 'lg';
// Replicate variant type
type AutocompleteVariant = 'outlined' | 'soft' | 'plain';
// Add Color type matching Select
type AutocompleteColor =
  | 'primary'
  | 'neutral'
  | 'danger'
  | 'success'
  | 'warning';

// --- Styling logic fully aligned with Select.tsx --- //
const getAutocompleteContainerClasses = (
  size: AutocompleteSize,
  variant: AutocompleteVariant,
  error: boolean,
  disabled: boolean | undefined,
  focused: boolean,
  color: AutocompleteColor // Added color prop
) => {
  const baseClasses =
    'flex items-stretch w-full rounded-lg transition-all duration-200';

  // Use error prop to override color to danger
  const effectiveColor = error ? 'danger' : color;

  const colorClasses = {
    primary: {
      outlined: [
        'border border-neutral-200 bg-white',
        focused
          ? 'border-primary-500 ring-1 ring-primary-500/20'
          : 'hover:border-primary-500',
        disabled
          ? 'border-neutral-100 bg-neutral-50 opacity-50 cursor-not-allowed'
          : '',
      ].join(' '),
      soft: [
        'bg-primary-50',
        focused ? 'bg-primary-100/70' : 'hover:bg-primary-100/50',
        disabled ? 'bg-neutral-50 opacity-50 cursor-not-allowed' : '',
      ].join(' '),
      plain: [
        'bg-transparent',
        focused ? 'bg-primary-50' : 'hover:bg-primary-50/50',
        disabled ? 'bg-transparent opacity-50 cursor-not-allowed' : '',
      ].join(' '),
    },
    neutral: {
      outlined: [
        'border border-neutral-200 bg-white',
        focused
          ? 'border-neutral-500 ring-1 ring-neutral-500/20'
          : 'hover:border-neutral-500',
        disabled
          ? 'border-neutral-100 bg-neutral-50 opacity-50 cursor-not-allowed'
          : '',
      ].join(' '),
      soft: [
        'bg-neutral-100/50',
        focused ? 'bg-neutral-100' : 'hover:bg-neutral-100',
        disabled ? 'bg-neutral-50 opacity-50 cursor-not-allowed' : '',
      ].join(' '),
      plain: [
        'bg-transparent',
        focused ? 'bg-neutral-100/50' : 'hover:bg-neutral-50',
        disabled ? 'bg-transparent opacity-50 cursor-not-allowed' : '',
      ].join(' '),
    },
    danger: {
      // Error case maps to danger
      outlined: [
        'border border-neutral-200 bg-white',
        focused
          ? 'border-red-500 ring-1 ring-red-500/20'
          : 'hover:border-red-500',
        disabled
          ? 'border-neutral-100 bg-neutral-50 opacity-50 cursor-not-allowed'
          : '',
      ].join(' '),
      soft: [
        'bg-red-50',
        focused ? 'bg-red-100/70' : 'hover:bg-red-100/50',
        disabled ? 'bg-neutral-50 opacity-50 cursor-not-allowed' : '',
      ].join(' '),
      plain: [
        'bg-transparent',
        focused ? 'bg-red-50' : 'hover:bg-red-50/50',
        disabled ? 'bg-transparent opacity-50 cursor-not-allowed' : '',
      ].join(' '),
    },
    success: {
      outlined: [
        'border border-neutral-200 bg-white',
        focused
          ? 'border-green-500 ring-1 ring-green-500/20'
          : 'hover:border-green-500',
        disabled
          ? 'border-neutral-100 bg-neutral-50 opacity-50 cursor-not-allowed'
          : '',
      ].join(' '),
      soft: [
        'bg-green-50',
        focused ? 'bg-green-100/70' : 'hover:bg-green-100/50',
        disabled ? 'bg-neutral-50 opacity-50 cursor-not-allowed' : '',
      ].join(' '),
      plain: [
        'bg-transparent',
        focused ? 'bg-green-50' : 'hover:bg-green-50/50',
        disabled ? 'bg-transparent opacity-50 cursor-not-allowed' : '',
      ].join(' '),
    },
    warning: {
      outlined: [
        'border border-neutral-200 bg-white',
        focused
          ? 'border-yellow-500 ring-1 ring-yellow-500/20'
          : 'hover:border-yellow-500',
        disabled
          ? 'border-neutral-100 bg-neutral-50 opacity-50 cursor-not-allowed'
          : '',
      ].join(' '),
      soft: [
        'bg-yellow-50',
        focused ? 'bg-yellow-100/70' : 'hover:bg-yellow-100/50',
        disabled ? 'bg-neutral-50 opacity-50 cursor-not-allowed' : '',
      ].join(' '),
      plain: [
        'bg-transparent',
        focused ? 'bg-yellow-50' : 'hover:bg-yellow-50/50',
        disabled ? 'bg-transparent opacity-50 cursor-not-allowed' : '',
      ].join(' '),
    },
  };

  const variantClasses = colorClasses[effectiveColor][variant];

  const sizeClasses = {
    sm: 'min-h-8 text-xs',
    md: 'min-h-10 text-sm',
    lg: 'min-h-12 text-sm',
  };

  return twMerge(baseClasses, variantClasses, sizeClasses[size]);
};

const getAutocompletePaddingClasses = (
  size: AutocompleteSize,
  hasStartDecorator: boolean
  // End decorator padding handled separately if needed by icon placement
) => {
  const basePadding = {
    sm: 'py-1.5',
    md: 'py-2',
    lg: 'py-3',
  };

  const startPadding = {
    sm: hasStartDecorator ? 'pl-2' : 'pl-3',
    md: hasStartDecorator ? 'pl-2' : 'pl-3',
    lg: hasStartDecorator ? 'pl-3' : 'pl-4',
  };

  // Autocomplete needs padding for the end icon
  const endPadding = {
    sm: 'pr-8', // Approximate padding for caret
    md: 'pr-9',
    lg: 'pr-10',
  };

  return twMerge(basePadding[size], startPadding[size], endPadding[size]);
};

const getAutocompleteDecoratorClasses = (
  size: AutocompleteSize,
  disabled: boolean | undefined,
  position: 'start' | 'end'
) => {
  const baseClasses = 'flex items-center pointer-events-none'; // Added pointer-events-none
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  const paddingClasses = {
    start: {
      sm: 'pl-3',
      md: 'pl-3',
      lg: 'pl-4',
    },
    end: {
      sm: 'pr-3',
      md: 'pr-3',
      lg: 'pr-4',
    },
  };
  const colorClasses = disabled ? 'text-neutral-300' : 'text-neutral-500';

  // Position absolutely for decorators within the container
  const positionClasses = {
    start: 'absolute left-0 top-0 bottom-0',
    end: 'absolute right-0 top-0 bottom-0',
  };

  return twMerge(
    baseClasses,
    sizeClasses[size],
    paddingClasses[position][size],
    positionClasses[position],
    colorClasses
  );
};
// --- End of copied logic --- //

export interface AutocompleteProps {
  options: OptionType[];
  value: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  startDecorator?: React.ReactNode;
  dropdownTitle?: string;
  hideOptionsHeader?: boolean;
  className?: string;
  dropdownClassName?: string;
  disabled?: boolean;
  showCreateNewOption?: boolean;
  handleCreateNew?: () => void;
  emptyOptionsText?: string;
  hideEndDecorator?: boolean;
  id?: string;
  sx?: React.CSSProperties;
  size?: AutocompleteSize;
  error?: boolean;
  variant?: AutocompleteVariant;
  color?: AutocompleteColor;
  inputValue?: string;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  loadingText?: string;
}

const Autocomplete = ({
  options,
  value = [],
  onChange,
  placeholder = 'Start typing to search...',
  startDecorator,
  dropdownTitle = 'Select Options',
  hideOptionsHeader = false,
  className = '',
  dropdownClassName = '',
  showCreateNewOption = false,
  handleCreateNew,
  emptyOptionsText = 'No matching options',
  disabled = false,
  hideEndDecorator = false,
  id,
  sx,
  size = 'md',
  error = false,
  variant = 'outlined',
  color = 'neutral',
  inputValue: controlledInputValue,
  onInputChange,
  loading = false,
  loadingText = 'Loading...',
}: AutocompleteProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownPositionReady, setIsDropdownPositionReady] = useState(false);

  const isControlled = controlledInputValue !== undefined;
  const currentInputValue = isControlled ? controlledInputValue : inputValue;

  const filteredOptions = useMemo(() => {
    if (isControlled || !currentInputValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(currentInputValue.toLowerCase())
    );
  }, [options, currentInputValue, isControlled]);

  // --- Define ALL event handlers BEFORE useEffect hooks --- //

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsFocused(false);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 256;
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      setPosition({
        top: shouldOpenUpward
          ? rect.top + scrollY - dropdownHeight - 4
          : rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: rect.width,
      });

      // Mark positioning as complete
      setIsDropdownPositionReady(true);
    }
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onInputChange?.(event);

      if (!isOpen && !disabled) {
        setIsOpen(true);
      }
    },
    [onInputChange, isOpen, disabled]
  );

  const handleOptionToggle = useCallback(
    (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
      // No focus change needed here now
    },
    [value, onChange]
  );

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    // Rely on outside click handler (useEffect below)
  }, []);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      const nextOpenState = !isOpen;
      if (nextOpenState) {
        // Reset position ready state when opening
        setIsDropdownPositionReady(false);
        inputRef.current?.focus();
        setIsFocused(true);
      } else {
        setIsFocused(false);
      }
      setIsOpen(nextOpenState);
    }
  }, [disabled, isOpen, inputRef]);

  // --- useEffect Hooks --- //

  // Effect for dropdown positioning on open/scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition(); // Initial position
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  const currentStartDecorator = startDecorator;

  return (
    <div ref={containerRef} className={'relative'} style={sx}>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <div
            ref={inputWrapperRef}
            className={twMerge(
              getAutocompleteContainerClasses(
                size,
                variant,
                error,
                disabled,
                isFocused || isOpen,
                color
              ),
              'relative',
              className
            )}
            onClick={handleToggle}
          >
            {currentStartDecorator && (
              <span
                className={getAutocompleteDecoratorClasses(
                  size,
                  disabled,
                  'start'
                )}
              >
                {currentStartDecorator}
              </span>
            )}

            <Input
              id={id}
              ref={inputRef}
              value={currentInputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              disabled={disabled}
              className={twMerge(
                'flex-grow bg-transparent border-none focus:ring-0 h-full',
                getAutocompletePaddingClasses(size, !!currentStartDecorator)
              )}
            />

            {!hideEndDecorator && (
              <span
                className={getAutocompleteDecoratorClasses(
                  size,
                  disabled,
                  'end'
                )}
              >
                <span
                  className={twMerge(
                    'flex items-center transition-transform duration-200 ease-out',
                    isOpen ? '-rotate-90' : 'rotate-0'
                  )}
                >
                  <Icon
                    icon={faCaretDown}
                    className="w-3.5 h-3.5 text-neutral-500"
                    data-testid={`${id}-autocomplete-caret-icon`}
                  />
                </span>
              </span>
            )}
          </div>

          {isOpen &&
            !disabled &&
            isDropdownPositionReady &&
            createPortal(
              <div
                ref={dropdownRef}
                data-autocomplete-portal-id={id}
                className={twMerge(
                  'fixed z-[9999] flex flex-col max-h-64 overflow-hidden bg-white border border-neutral-300 rounded-lg shadow-md',
                  dropdownClassName
                )}
                style={{
                  top: position.top,
                  left: position.left,
                  width: position.width,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {!hideOptionsHeader && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-300 shrink-0">
                    <div>
                      <Icon
                        icon={faFolderBookmark}
                        className="w-4 h-4 text-neutral-600 pr-2.5"
                      />
                      <span className="text-sm font-bold text-neutral-800">
                        {dropdownTitle}
                      </span>
                    </div>
                    {showCreateNewOption ? (
                      <div
                        className="flex items-center gap-1.5 cursor-pointer"
                        onClick={handleCreateNew}
                      >
                        <Icon icon={faPlus} className="w-3 h-3 text-sky-900" />
                        <p className="text-sm font-bold text-sky-900">New</p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleClose}
                        className="text-neutral-500 hover:text-neutral-700"
                        aria-label="Close dropdown"
                        data-testid="autocomplete-close-button"
                      >
                        <Icon icon={faXmark} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                <div className="flex-grow overflow-y-auto p-1">
                  {loading ? (
                    <div className="px-3 py-1.5 text-sm text-neutral-600 flex items-center gap-1">
                      <CircularProgress size="sm" />
                      {loadingText}
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="px-3 py-1.5 text-sm text-neutral-600">
                      {emptyOptionsText}
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center gap-2 text-left p-2 text-sm rounded-md hover:bg-neutral-100 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionToggle(option.value);
                        }}
                        data-testid={`${id}-option-${option.value}`}
                      >
                        <Checkbox
                          data-testid={`${id}-option-${option.value}`}
                          checked={value.includes(option.value)}
                          onChange={() => {
                            handleOptionToggle(option.value);
                          }}
                          size="sm"
                        />
                        <span className="flex-1 overflow-hidden text-ellipsis text-neutral-700">
                          {option.label}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>,
              document.body
            )}
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default Autocomplete;
